import {observer} from "mobx-react";
import React, {useCallback, useEffect, useState} from "react";
import {FontBody1} from "../../components/fonts/font-body1/font-body1";
import {FontBody2} from "../../components/fonts/font-body2/font-body2";
import {FONT_COLOR, FONT_VARIANT} from "../../components/fonts/font-props";
import {FormFieldLine} from "../../components/form-field-line/form-field-line";
import {FormField} from "../../components/form-field/form-field";
import Hr from "../../components/hr/hr";
import {Input} from "../../components/inputs/input/input";
import {Toggle} from "../../components/inputs/toggle/toggle";
import BasePageLayout from "../../components/layouts/base-page-layout/base-page-layout";
import EditorPageLayout from "../../components/layouts/editor-page/editor-page-layout";
import ListLayout from "../../components/layouts/list-layout/list-layout";
import {ConfirmModal} from "../../components/modal/components/confirm-modal/confirm-modal";
import useNavigationBlocker from "../../hooks/navigation-blocker-hook";
import useValidationGroup from "../../hooks/validation-group/validation-group-hook";
import PreviewSvg from '../../images/previews/email-notification-preview.svg'
import {BCC_RECEIVER_ENUM} from "../../services/api/email-setting-service";
import emailNotificationsMainStore from "./stores/email-notifications-main-store";
import './email-notification-page.scss';

const EmailNotifications: React.FC = observer(() => {

	useEffect(()=>{
		emailNotificationsMainStore.load()
	},[]);
	
	const emailSettingStore = emailNotificationsMainStore.emailSettingStore;
	const emailCustomizationStore = emailNotificationsMainStore.emailCustomizationStore;
	const validationGroup = useValidationGroup();
 
	const [isShowUnsaved, setIsShowUnsaved] = useState(false);
	const { retryNavigation } = useNavigationBlocker(
		() => emailNotificationsMainStore.hasChanges(),
		useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
	);
	return (
		<div className={'email-notification-page'}>
			<BasePageLayout title='Settings' subTitles={['Emails','Notifications']} instruction='Customize your return email notifications.'>
				{isShowUnsaved &&
					<ConfirmModal 
						cancelButton={{
							title: 'Cancel',
							onClick: () => setIsShowUnsaved(false)
						}}
						okButton={{
							title: 'Discard',
							onClick: () => {
								emailNotificationsMainStore.discardChanges();
								setIsShowUnsaved(false);
								retryNavigation();
							}
						}}
					/>
				}
				<EditorPageLayout
					toolbar={{
						hasBack: false,
						hasLocalesSelector: false,
						saveButton: {
							hasChanges: emailNotificationsMainStore.hasChanges(),
							onClick: ()=> emailNotificationsMainStore.save(),
							disabled: !emailNotificationsMainStore.hasChanges() ||
                                      emailNotificationsMainStore.isSaving ||
                                      !validationGroup.isValid(),
                            isValid: validationGroup.isValid(),
						},
						isLoading: emailNotificationsMainStore.isLoading
						
					}}>
					<ListLayout>
                        <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>Send From</FontBody2>
                        <FontBody2 color={FONT_COLOR.PRIMARY3_50}>Customer notifications will be sent from this email address.</FontBody2>
                        <div className={'from-grid'}>
                            <div className={'from-fields'}>
                            <FormField label='"From" Name'>
                                <Input value={emailCustomizationStore.senderName} onChange={()=> {}} disabled={true}/>
                            </FormField>
                            <div>
                            
                            </div>
                            <FormField label='"From" Email Address ' required={true}
                            
                            >
                                <FontBody2 color={FONT_COLOR.NEUTRAL3}>
                                    The sender’s email address. To change this address please contact&nbsp;
                                    <a href="mailto:support@returngo.ai">customer support</a>.<br/>
                                    <a rel="noreferrer" target='_blank'
                                       href="https://returngo.ai/go/kb/from-address">Learn more</a>
                                </FontBody2>
                                <Input
                                    placeholder='Email Address'
                                    value={emailCustomizationStore.senderAddress}
                                    onChange={emailCustomizationStore.setSenderAddress}
                                    disabled={emailCustomizationStore.isFromAddressDisabled()}
                                    required
                                    validationGroup={validationGroup}
                                />
                            </FormField>
                            </div>
                            <div  className={'img-preview'}>
                                <img alt={''} src={PreviewSvg}/>
                            </div>
                        </div>
                        <Hr/>
						<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>Reply to</FontBody2>
						<FontBody2 color={FONT_COLOR.PRIMARY3_50}>The email address to which emails will be sent when customers click the "Reply" button.</FontBody2>
						<FormFieldLine label='"Reply to" Email Address' >
							<Input placeholder='Email Address'
								value={emailCustomizationStore.replyToAddress}
						        disabled={true}
								onChange={()=> {} }
							/>
						</FormFieldLine>
						
						<Hr/>
						
						<FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>Get a Notification Copy</FontBody1>
						<FontBody2 color={FONT_COLOR.PRIMARY3_50}>Choose which notifications you would like to receive a copy of when they are sent to customers.</FontBody2>
						<FormFieldLine
							label={'Email Address to Notify'}
						    note={'The email address to which we’ll send you a copy of the notifications.'}
							required
						>
							<Input
								placeholder='Email Address'
								value={emailSettingStore.bccEmail}
								onChange={emailSettingStore.setBccEmail}
                                validationCallback={emailSettingStore.validateBccEmail}
                                required
                                validationGroup={validationGroup}
							/>
						</FormFieldLine>
						
						<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>Notification Copies</FontBody2>
						<FontBody2 color={FONT_COLOR.PRIMARY3_50}>Set which RMA status updates you would like to receive copies of.</FontBody2>
						<FormFieldLine
							label='In Transit'
							note=''
						>
							<Toggle label=''
								checked={emailSettingStore.getShipmentStatusInTransitUpdate(BCC_RECEIVER_ENUM.MERCHANT)}
								onChange={value=> emailSettingStore.toggleShipmentStatusInTransitUpdateByReceiver(BCC_RECEIVER_ENUM.MERCHANT)}/>
						</FormFieldLine>
						<FormFieldLine
							label='Shipment Received (Delivered)'
							note=''
						>
							<Toggle label=''
							        checked={emailSettingStore.getShipmentStatusShipmentReceivedUpdateByReceiver(BCC_RECEIVER_ENUM.MERCHANT)}
							        onChange={value=> emailSettingStore.toggleShipmentStatusShipmentReceivedByReceiver(BCC_RECEIVER_ENUM.MERCHANT) }
							/>
						</FormFieldLine>
						<FormFieldLine
							label='All Other Status Updates'
							note=''
						>
							<Toggle label=''
								checked={emailSettingStore.bccOwner}
								onChange={value=> emailSettingStore.setBccOwner(value) }/>
						</FormFieldLine>
						
						<Hr/>
						
						<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>Customer Shipment Notifications</FontBody2>
						<FontBody2 color={FONT_COLOR.PRIMARY3_50}>Set the default for which return shipment status update notifications are sent to customers (based on updates from supported integrations). These can be changed per return method.</FontBody2>
						<FormFieldLine
							label='In Transit'
							note=''
						>
							<Toggle label=''
								checked={emailSettingStore.getShipmentStatusInTransitUpdate(BCC_RECEIVER_ENUM.CUSTOMER)}
								onChange={value => emailSettingStore.toggleShipmentStatusInTransitUpdateByReceiver(BCC_RECEIVER_ENUM.CUSTOMER) }
							/>
						</FormFieldLine>
						<FormFieldLine
							label='Shipment Received (Delivered)'
							note=''
						>
							<Toggle label=''
							        checked={emailSettingStore.getShipmentStatusShipmentReceivedUpdateByReceiver(BCC_RECEIVER_ENUM.CUSTOMER)}
							        onChange={value=> emailSettingStore.toggleShipmentStatusShipmentReceivedByReceiver(BCC_RECEIVER_ENUM.CUSTOMER) }
							/>
						</FormFieldLine>
						
					</ListLayout>
				</EditorPageLayout>
			</BasePageLayout>
		</div>
	);
});

export default EmailNotifications;
